/**
 * SurveyWalkthrough — Guided step-by-step survey experience.
 *
 * Walks the surveyor through each facility area and domain with detailed
 * narrative about what to look for (CMS Appendix L / CfCs, TJC, AAAHC).
 * Photos, documents, and findings are auto-tagged with the current area
 * and domain context so the surveyor doesn't have to manually classify.
 *
 * Route: /visits/:visitId/survey/walkthrough
 */

import { useState, useEffect, useCallback, useMemo, useRef, useContext } from 'react'
import { useParams, Link, useSearchParams } from 'react-router-dom'
import { HostHandoffSplash } from '../../components/survey/HostHandoffSplash'
import {
  ArrowLeft,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Pencil,
  Trash2,
  CheckCircle,
  SkipForward,
  AlertTriangle,
  MapPin,
  List,
  FileCheck,
} from 'lucide-react'
import { PhotoCapture } from '../../components/survey/PhotoCapture'
import { SyncStatus } from '../../components/survey/SyncStatus'
import { FieldAppShell } from '../../components/survey/FieldAppShell'
import { AreaBriefing } from '../../components/survey/AreaBriefing'
import { PhotoChecklist } from '../../components/survey/PhotoChecklist'
import { PhotoReview } from '../../components/survey/PhotoReview'
import { AuthContext } from '../../context/AuthContext'
import { blobToDataUrl } from '../../lib/offline/photoPreview'
import { FindingCounter } from '../../components/survey/FindingCounter'
import { detectPatterns, detectConditionLevel, type ConditionAlert, type AnalyzableFinding } from '../../lib/findingAnalysis'
import { surveyStore, type PendingFinding } from '../../lib/offline/surveyStore'
import { enqueueFinding } from '../../lib/services/surveySync'
import {
  getSurvey,
  getWalkthroughState,
  saveWalkthroughState,
  type SurveySession,
  type SeverityLevel,
  type WalkthroughState,
} from '../../lib/services/survey'
import { downloadEvidenceBlob } from '../../lib/services/evidence'
import { subscribeToSurveyFindings, type RealtimeFinding } from '../../lib/services/surveyRealtime'
import {
  SURVEY_WALKTHROUGH,
  getInspectionChecklistPrompts,
  totalInspectionItems,
  type AreaWalkthrough,
  type DomainSection,
  type InspectionItem,
} from '../../lib/surveyGuide'
import { computeWalkthroughAssessmentCoverage } from '../../lib/walkthroughAssessmentCoverage'

// ── Step status tracking ────────────────────────────────────────────────────

type StepStatus = 'pending' | 'finding' | 'skipped' | 'clear'
type ChecklistResponse = 'yes' | 'no' | 'na' | null
type SavedChecklistResponse = Exclude<ChecklistResponse, null>
type PhotoReviewAnnotation = import('../../components/survey/PhotoReview').PhotoAnnotation

interface StepState {
  status: StepStatus
  findingIds: string[]
  photoIds: string[]
  notes: string
  checklistResponses?: Record<string, SavedChecklistResponse>
  promptEvidence?: Record<string, PromptEvidence>
}

interface PromptEvidence {
  note: string
  photoIds: string[]
  severity?: 1 | 2 | 3
  photoAnnotations?: Record<string, PhotoReviewAnnotation[]>
}

function parseLastUpdated(value: string | undefined): number | null {
  if (!value) return null
  const timestamp = Date.parse(value)
  return Number.isFinite(timestamp) ? timestamp : null
}

function stepRichness(step?: StepState): number {
  if (!step) return 0
  const checklistCount = Object.keys(step.checklistResponses ?? {}).length
  const promptEvidenceEntries = Object.values(step.promptEvidence ?? {})
  const promptEvidenceCount = promptEvidenceEntries.length
  const promptPhotoCount = promptEvidenceEntries.reduce((total, evidence) => total + evidence.photoIds.length, 0)
  const annotationCount = promptEvidenceEntries.reduce(
    (total, evidence) => total + Object.values(evidence.photoAnnotations ?? {}).reduce((sum, annotations) => sum + annotations.length, 0),
    0,
  )
  return checklistCount + promptEvidenceCount + step.photoIds.length + promptPhotoCount + annotationCount
}

function walkthroughRichness(state?: WalkthroughState | null): number {
  if (!state) return 0
  return Object.values(state.stepStates ?? {}).reduce<number>((total, step) => total + stepRichness(step), 0)
}

function selectPreferredWalkthroughState(
  localSaved?: WalkthroughState | null,
  remoteSaved?: WalkthroughState | null,
): WalkthroughState | null {
  const hasLocal = Boolean(localSaved && Object.keys(localSaved.stepStates ?? {}).length > 0)
  const hasRemote = Boolean(remoteSaved && Object.keys(remoteSaved.stepStates ?? {}).length > 0)
  if (!hasLocal && !hasRemote) return null
  if (!hasLocal) return remoteSaved ?? null
  if (!hasRemote) return localSaved ?? null

  const localTimestamp = parseLastUpdated(localSaved?.lastUpdated)
  const remoteTimestamp = parseLastUpdated(remoteSaved?.lastUpdated)
  if (localTimestamp !== null && remoteTimestamp !== null && remoteTimestamp > localTimestamp) {
    return remoteSaved ?? null
  }
  if (localTimestamp !== null && remoteTimestamp !== null && localTimestamp > remoteTimestamp) {
    return localSaved ?? null
  }

  return walkthroughRichness(remoteSaved) > walkthroughRichness(localSaved)
    ? (remoteSaved ?? null)
    : (localSaved ?? null)
}

// `ChecklistPrompt` and `getInspectionChecklistPrompts` now live in
// `surveyGuide.ts` (#1227). Consumers in this file use
// `RenderedChecklistPrompt` directly via the imports above.

/** Unique key for each inspection item */
function stepKey(areaIdx: number, sectionIdx: number, itemIdx: number): string {
  return `${areaIdx}-${sectionIdx}-${itemIdx}`
}

// `getInspectionChecklistPrompts` was migrated to `surveyGuide.ts` in PR-A
// (#1227). It is re-exported for any external test/utility callers that
// previously imported from this module path.
export { getInspectionChecklistPrompts }

type AreaPhase = 'briefing' | 'photos' | 'inspection'

interface WalkthroughPosition {
  areaIdx: number
  sectionIdx: number
  itemIdx: number
  areaPhase: AreaPhase
  currentRoom: string | null
}

export function getNextWalkthroughPosition(
  areaIdx: number,
  sectionIdx: number,
  itemIdx: number,
  currentRoom: string | null,
): WalkthroughPosition {
  const area = SURVEY_WALKTHROUGH[areaIdx]
  if (!area) {
    return { areaIdx, sectionIdx, itemIdx, areaPhase: 'inspection', currentRoom }
  }

  const section = area.sections[sectionIdx]
  if (section && itemIdx < section.items.length - 1) {
    return { areaIdx, sectionIdx, itemIdx: itemIdx + 1, areaPhase: 'inspection', currentRoom }
  }

  if (sectionIdx < area.sections.length - 1) {
    return { areaIdx, sectionIdx: sectionIdx + 1, itemIdx: 0, areaPhase: 'inspection', currentRoom }
  }

  if (areaIdx < SURVEY_WALKTHROUGH.length - 1) {
    return { areaIdx: areaIdx + 1, sectionIdx: 0, itemIdx: 0, areaPhase: 'briefing', currentRoom: null }
  }

  return { areaIdx, sectionIdx, itemIdx, areaPhase: 'inspection', currentRoom }
}

// ── Component ───────────────────────────────────────────────────────────────

export function SurveyWalkthrough() {
  const { visitId } = useParams<{ visitId: string }>()
  const authContext = useContext(AuthContext)
  const [searchParams, setSearchParams] = useSearchParams()
  const resumeFacility = searchParams.get('resume') === '1' ? searchParams.get('facility') ?? '' : null
  const preferCompletedSession = searchParams.get('resume') === 'completed'
  const completedResumeRequestedRef = useRef(preferCompletedSession)
  const [session, setSession] = useState<SurveySession | null>(null)
  const [sessionLoading, setSessionLoading] = useState(true)
  const isFieldSurveyor = authContext?.viewerContext?.role === 'FieldSurveyor'

  // Strip only the host-handoff resume hint once the session loads so the
  // splash does not re-render on re-mount. Keep `resume=completed` because it
  // is an explicit report-review mode and prevents accidental active-session
  // re-entry.
  useEffect(() => {
    if (session && searchParams.get('resume') === '1') {
      const next = new URLSearchParams(searchParams)
      next.delete('resume')
      next.delete('facility')
      setSearchParams(next, { replace: true })
    }
  }, [session, searchParams, setSearchParams])

  // Navigation state
  const [areaIdx, setAreaIdx] = useState(0)
  const [sectionIdx, setSectionIdx] = useState(0)
  const [itemIdx, setItemIdx] = useState(0)
  const [showAreaNav, setShowAreaNav] = useState(false)
  const [showBottomNav, setShowBottomNav] = useState(false)
  const [expandedNavArea, setExpandedNavArea] = useState<number | null>(null)

  // Area phase: briefing → photos → inspection
  const [areaPhase, setAreaPhase] = useState<AreaPhase>('briefing')

  // Room selection for repeatable areas (e.g., OR 1, OR 2)
  const [currentRoom, setCurrentRoom] = useState<string | null>(null)

  // Current step interaction
  const [showNarrative, setShowNarrative] = useState(true)
  const [observation, setObservation] = useState('')
  const [severity, setSeverity] = useState<SeverityLevel>(2)
  const [photoIds, setPhotoIds] = useState<string[]>([])
  // Blob-URL mirror of photoIds so the inspection-item evidence block can show
  // tappable thumbnails that mirror the guided-survey PhotoChecklist pattern.
  // Hydrated from surveyStore when photoIds changes (including when the user
  // navigates back into a completed step).
  const [capturedTiles, setCapturedTiles] = useState<Array<{ photoId: string; url: string }>>([])
  const [promptEvidence, setPromptEvidence] = useState<Record<string, PromptEvidence>>({})
  const [promptEvidenceOpen, setPromptEvidenceOpen] = useState<Record<string, boolean>>({})
  const hydratedPhotoIds = useMemo(
    () => [...new Set([
      ...photoIds,
      ...Object.values(promptEvidence).flatMap((evidence) => evidence.photoIds),
    ])],
    [photoIds, promptEvidence],
  )
  const [isSaving, setIsSaving] = useState(false)
  const [checklistResponse, setChecklistResponse] = useState<ChecklistResponse>(null)
  const [checklistResponses, setChecklistResponses] = useState<Record<string, SavedChecklistResponse>>({})
  const [evidenceError, setEvidenceError] = useState<string | null>(null)
  const [showCompletionSummary, setShowCompletionSummary] = useState(false)
  const [reviewingCompletedItem, setReviewingCompletedItem] = useState(false)

  // Photo review overlay
  const [reviewPhoto, setReviewPhoto] = useState<{ url: string; photoId: string; requirePin: boolean; promptId?: string } | null>(null)
  const [reviewAnnotations, setReviewAnnotations] = useState<PhotoReviewAnnotation[]>([])

  // Load existing annotations when opening a photo for review.
  // requirePin defaults false — only deficiency-documenting angles (closeup)
  // should force the surveyor to drop a pin. Baseline shots (entry view, wall
  // views, equipment overviews) can be saved without annotation.
  const openReview = useCallback(async (url: string, photoId: string, requirePin = false, promptId?: string) => {
    const photo = await surveyStore.getPhoto(photoId).catch(() => undefined)
    const fallbackAnnotations = promptId
      ? promptEvidence[promptId]?.photoAnnotations?.[photoId]
      : undefined
    setReviewAnnotations(
      (photo?.annotations as PhotoReviewAnnotation[]) ?? fallbackAnnotations ?? [],
    )
    setReviewPhoto({ url, photoId, requirePin, promptId })
  }, [promptEvidence])

  // Shared persistence for both save + dismiss paths from PhotoReview. Pins
  // must survive overlay close/reopen so the user can keep adding to a photo
  // across sessions (or after validation blocked an explicit Save).
  const persistReviewAnnotations = useCallback(
    async (photoId: string, annotations: PhotoReviewAnnotation[]) => {
      const photo = await surveyStore.getPhoto(photoId).catch(() => undefined)
      if (!photo) return
      await surveyStore.savePhoto({
        ...photo,
        annotations: annotations.map((a) => ({
          id: a.id,
          x: a.x,
          y: a.y,
          comment: a.comment,
          severity: a.severity,
          created_at: a.created_at,
        })),
      })
      // Keep the open-review snapshot in sync so subsequent in-overlay saves
      // see the freshly-persisted annotations instead of the stale copy.
      setReviewAnnotations(annotations)
    },
    [],
  )

  const applyPinEvidenceToPrompt = useCallback(
    (promptId: string | undefined, photoId: string, annotations: PhotoReviewAnnotation[]) => {
      if (!promptId) return
      const strongestCommentedPin = annotations
        .filter((annotation) => annotation.comment.trim().length > 0)
        .sort((a, b) => b.severity - a.severity)[0]
      if (!strongestCommentedPin) return

      setPromptEvidence((prev) => {
        const existing = prev[promptId] ?? { note: '', photoIds: [] }
        return {
          ...prev,
          [promptId]: {
            ...existing,
            note: existing.note.trim() ? existing.note : strongestCommentedPin.comment.trim(),
            photoIds: existing.photoIds.includes(photoId) ? existing.photoIds : [...existing.photoIds, photoId],
            severity: strongestCommentedPin.severity,
            photoAnnotations: {
              ...(existing.photoAnnotations ?? {}),
              [photoId]: annotations.map((annotation) => ({ ...annotation })),
            },
          },
        }
      })
      setEvidenceError(null)
    },
    [],
  )

  const hydrateStepLocalState = useCallback((saved?: StepState) => {
    if (!saved) {
      setChecklistResponses({})
      setChecklistResponse(null)
      setObservation('')
      setPhotoIds([])
      setPromptEvidence({})
      setPromptEvidenceOpen({})
      return
    }

    const responses = saved.checklistResponses ?? {}
    const responseValues = Object.values(responses)
    const singlePromptResponse = responseValues.length === 1 ? responseValues[0] : null
    const fallbackEvidence = Object.keys(saved.promptEvidence ?? {}).length > 0
      ? saved.promptEvidence ?? {}
      : Object.fromEntries(
        Object.entries(responses)
          .filter(([, response]) => response === 'no')
          .map(([promptId]) => [promptId, {
            note: saved.notes ?? '',
            photoIds: saved.photoIds ?? [],
          }]),
      )
    setChecklistResponses(responses)
    setChecklistResponse(
      saved.status === 'clear'
        ? 'yes'
        : saved.status === 'skipped'
          ? 'na'
          : saved.status === 'finding'
            ? 'no'
            : singlePromptResponse,
    )
    setObservation(saved.notes ?? '')
    setPhotoIds(saved.photoIds ?? [])
    setPromptEvidence(fallbackEvidence)
    setPromptEvidenceOpen(
      Object.fromEntries(
        Object.entries(responses)
          .filter(([, response]) => response === 'no')
          .map(([promptId]) => [promptId, true]),
      ),
    )
  }, [])

  const persistWalkthroughState = useCallback(async (state: {
    survey_id: string
    areaIdx: number
    sectionIdx: number
    itemIdx: number
    areaPhase?: AreaPhase
    currentRoom?: string | null
    stepStates: Record<string, StepState>
    lastUpdated: string
  }) => {
    await surveyStore.saveWalkthroughState(state)
    if (visitId) {
      await saveWalkthroughState(visitId, state.survey_id, state).catch(() => {
        // Local IndexedDB remains the offline source while the sync layer retries
        // normal evidence/finding uploads. Walkthrough state will be re-saved on
        // the next interaction.
      })
    }
  }, [visitId])

  // Regulatory alerts
  const [patternToast, setPatternToast] = useState<string | null>(null)
  const [conditionAlert, setConditionAlert] = useState<ConditionAlert | null>(null)

  // Step tracking
  const [stepStates, setStepStates] = useState<Record<string, StepState>>({})

  // Multi-surveyor remote findings — separate toast queue from permanent counter
  const [remoteFindings, setRemoteFindings] = useState<RealtimeFinding[]>([])
  const [remoteToasts, setRemoteToasts] = useState<RealtimeFinding[]>([])

  const [resumed, setResumed] = useState(false)
  const [walkthroughStateHydrated, setWalkthroughStateHydrated] = useState(false)

  // Load session — try API first, fall back to offline store
  useEffect(() => {
    if (!visitId) return
    let cancelled = false
    setSessionLoading(true)
    void (async () => {
      let nextSession: SurveySession | null = null
      try {
        nextSession = await getSurvey(visitId, { preferCompleted: preferCompletedSession })
        if (!nextSession && preferCompletedSession) {
          nextSession = await getSurvey(visitId)
        }
        if (!nextSession) {
          nextSession = (await surveyStore.getSessionByVisit(visitId)) ?? null
        }
      } catch {
        nextSession = (await surveyStore.getSessionByVisit(visitId).catch(() => undefined)) ?? null
      }
      if (!cancelled) {
        setSession(nextSession)
        setSessionLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [preferCompletedSession, visitId])

  // Restore walkthrough state from IndexedDB first, then server state after
  // logout/device changes when local state has been wiped.
  useEffect(() => {
    if (!session) return
    let cancelled = false
    setWalkthroughStateHydrated(false)
    void (async () => {
      const localSaved = await surveyStore.getWalkthroughState(session.survey_id).catch(() => undefined)
      const remoteSaved = await getWalkthroughState(visitId ?? '', session.survey_id).catch(() => null)
      const saved = selectPreferredWalkthroughState(localSaved, remoteSaved)
      if (cancelled) return
      if (saved && Object.keys(saved.stepStates).length > 0) {
        if (!localSaved || localSaved !== saved) {
          void surveyStore.saveWalkthroughState(saved)
        }
        setAreaIdx(saved.areaIdx)
        setSectionIdx(saved.sectionIdx)
        setItemIdx(saved.itemIdx)
        setAreaPhase(preferCompletedSession ? 'inspection' : saved.areaPhase ?? 'inspection')
        setCurrentRoom(saved.currentRoom ?? null)
        setStepStates(saved.stepStates)
        if (preferCompletedSession && session.status === 'completed') {
          setReviewingCompletedItem(true)
          setShowAreaNav(true)
        }
        setResumed(true)
        // Hydrate step-local display state for the restored position.
        // The useEffect([currentKey]) below only fires when currentKey changes;
        // if the restored position matches the initial position (both 0-0-0),
        // currentKey never changes and step responses would stay empty.
        const key = stepKey(saved.areaIdx, saved.sectionIdx, saved.itemIdx)
        const currentSaved = saved.stepStates[key]
        hydrateStepLocalState(currentSaved)
      } else if (preferCompletedSession && session.status === 'completed') {
        setShowCompletionSummary(true)
      }
      setWalkthroughStateHydrated(true)
    })()
    return () => {
      cancelled = true
    }
  }, [hydrateStepLocalState, preferCompletedSession, session, visitId])

  // Auto-dismiss resume toast
  useEffect(() => {
    if (!resumed) return
    const t = setTimeout(() => setResumed(false), 4000)
    return () => clearTimeout(t)
  }, [resumed])

  // Multi-surveyor realtime subscription
  useEffect(() => {
    if (!session) return
    const unsubscribe = subscribeToSurveyFindings(session.survey_id, {
      onFindingAdded: (finding) => {
        setRemoteFindings((prev) => [...prev, finding])
        setRemoteToasts((prev) => [...prev, finding])
      },
    })
    return unsubscribe
  }, [session])

  // Auto-dismiss remote finding toast after 5s
  useEffect(() => {
    if (remoteToasts.length === 0) return
    const t = setTimeout(() => setRemoteToasts((prev) => prev.slice(1)), 5000)
    return () => clearTimeout(t)
  }, [remoteToasts])

  // Keep capturedTiles (blob URLs) aligned with both step-level and prompt-
  // scoped photo references so resumed No-response evidence renders before
  // the finding is formally saved.
  useEffect(() => {
    let cancelled = false
    void (async () => {
      const next: Array<{ photoId: string; url: string }> = []
      for (const photoId of hydratedPhotoIds) {
        const photo = await surveyStore.getPhoto(photoId).catch(() => undefined)
        let url: string
        if (photo?.blob instanceof Blob) {
          // Convert to data URL — iOS may evict File-backed blob URLs under
          // memory pressure after the native camera returns control.
          url = await blobToDataUrl(photo.blob).catch(() => '')
        } else if (typeof photo?.blob === 'string') {
          url = photo.blob
        } else if (visitId) {
          let blob: Blob | null = null
          for (let attempt = 0; attempt < 4 && !blob; attempt += 1) {
            blob = await downloadEvidenceBlob(visitId, photoId).catch(() => null)
            if (blob || cancelled) break
            await new Promise((resolve) => window.setTimeout(resolve, 500))
          }
          if (!blob) continue
          url = await blobToDataUrl(blob).catch(() => '')
        } else {
          continue
        }
        if (!url) continue
        next.push({ photoId, url })
      }
      if (!cancelled) setCapturedTiles(next)
    })()
    return () => {
      cancelled = true
    }
  }, [authContext?.session?.access_token, hydratedPhotoIds, visitId])

  // Debounce-save walkthrough state to IndexedDB
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    if (!session) return
    if (!walkthroughStateHydrated || Object.keys(stepStates).length === 0) {
      return
    }
    if (saveTimer.current) clearTimeout(saveTimer.current)
    saveTimer.current = setTimeout(() => {
      void persistWalkthroughState({
        survey_id: session.survey_id,
        areaIdx,
        sectionIdx,
        itemIdx,
        areaPhase,
        currentRoom,
        stepStates,
        lastUpdated: new Date().toISOString(),
      })
    }, 500)
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current) }
  }, [session, walkthroughStateHydrated, areaIdx, sectionIdx, itemIdx, areaPhase, currentRoom, stepStates, persistWalkthroughState])

  // Current position
  const currentArea: AreaWalkthrough | undefined = SURVEY_WALKTHROUGH[areaIdx]
  const currentSection: DomainSection | undefined = currentArea?.sections[sectionIdx]
  const currentItem: InspectionItem | undefined = currentSection?.items[itemIdx]
  const currentKey = stepKey(areaIdx, sectionIdx, itemIdx)
  const currentState = stepStates[currentKey]

  // Promote in-progress evidence edits into the canonical step map immediately.
  // Without this, a captured No-response photo only lived in local component
  // state until the finding was saved; moving forward/back reset local inputs
  // and made the thumbnail appear to disappear.
  useEffect(() => {
    if (!session) return
    const hasDraftEvidence =
      photoIds.length > 0 ||
      Object.keys(promptEvidence).length > 0 ||
      Object.keys(checklistResponses).length > 0 ||
      observation.trim().length > 0
    if (!hasDraftEvidence) return

    setStepStates((prev) => {
      const existing = prev[currentKey]
      const nextState: StepState = {
        status: existing?.status ?? (Object.values(checklistResponses).includes('no') ? 'pending' : 'pending'),
        findingIds: existing?.findingIds ?? [],
        photoIds,
        notes: observation.trim() || existing?.notes || '',
        checklistResponses,
        promptEvidence,
      }

      if (
        existing &&
        existing.status === nextState.status &&
        existing.notes === nextState.notes &&
        JSON.stringify(existing.findingIds) === JSON.stringify(nextState.findingIds) &&
        JSON.stringify(existing.photoIds) === JSON.stringify(nextState.photoIds) &&
        JSON.stringify(existing.checklistResponses ?? {}) === JSON.stringify(nextState.checklistResponses ?? {}) &&
        JSON.stringify(existing.promptEvidence ?? {}) === JSON.stringify(nextState.promptEvidence ?? {})
      ) {
        return prev
      }

      return {
        ...prev,
        [currentKey]: nextState,
      }
    })
  }, [checklistResponses, currentKey, observation, photoIds, promptEvidence, session])

  // Hydrate local checklist display state from stepStates when the step
  // changes (goNext/goPrev reset local state via resetStepInputs; this
  // effect re-populates from the canonical stepStates record so
  // previously-answered items show their saved response when re-entered).
  const currentSaved = stepStates[currentKey]
  useEffect(() => {
    if (!walkthroughStateHydrated && !currentSaved) return
    hydrateStepLocalState(currentSaved)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentKey, currentSaved, walkthroughStateHydrated])

  // Scroll to top of page on every step change. Belt-and-suspenders for the
  // imperative scrollToTop() called from goNext/goPrev — this effect runs
  // after React commits the new step content so iOS Safari layout shifts
  // can't strand the user mid-page on the previous item's scroll position.
  useEffect(() => {
    const scrollAll = () => {
      if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
        window.scrollTo({ top: 0, behavior: 'auto' })
      }
      if (typeof document !== 'undefined') {
        if (typeof document.documentElement?.scrollTo === 'function') {
          document.documentElement.scrollTo({ top: 0, behavior: 'auto' })
        }
        if (typeof document.body?.scrollTo === 'function') {
          document.body.scrollTo({ top: 0, behavior: 'auto' })
        }
      }
    }
    scrollAll()
    // Re-fire after layout settles (sticky bars, image loads, etc.)
    const t = window.setTimeout(scrollAll, 80)
    return () => window.clearTimeout(t)
  }, [currentKey])

  const checklistPrompts = useMemo(
    () => currentItem ? getInspectionChecklistPrompts(currentItem) : [],
    [currentItem],
  )

  // Progress calculation
  const totalItems = useMemo(() => totalInspectionItems(), [])
  const completedItems = useMemo(
    () => Object.values(stepStates).filter((s) => s.status !== 'pending').length,
    [stepStates],
  )
  const assessmentCoverage = useMemo(
    () => computeWalkthroughAssessmentCoverage(stepStates),
    [stepStates],
  )
  const progressPct = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0
  const isLastInspectionItem = useMemo(() => {
    if (!currentArea || !currentSection) return false
    return (
      areaIdx === SURVEY_WALKTHROUGH.length - 1
      && sectionIdx === currentArea.sections.length - 1
      && itemIdx === currentSection.items.length - 1
    )
  }, [areaIdx, currentArea, currentSection, itemIdx, sectionIdx])

  const findingSummaries = useMemo(() => Object.entries(stepStates)
    .filter(([, state]) => state.status === 'finding')
    .map(([key, state]) => {
      const [ai, si, ii] = key.split('-').map(Number)
      const area = SURVEY_WALKTHROUGH[ai]
      const section = area?.sections[si]
      const item = section?.items[ii]
      return area && section && item
        ? { key, areaIdx: ai, sectionIdx: si, itemIdx: ii, area, section, item, state }
        : null
    })
    .filter((summary): summary is NonNullable<typeof summary> => summary !== null),
  [stepStates])

  // Domain finding counter (local + remote)
  const domainCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    // Count local findings from step states
    Object.entries(stepStates).forEach(([key, state]) => {
      if (state.status !== 'finding') return
      const [ai, si] = key.split('-').map(Number)
      const area = SURVEY_WALKTHROUGH[ai]
      const section = area?.sections[si]
      if (section) {
        counts[section.domain] = (counts[section.domain] || 0) + 1
      }
    })
    // Include remote findings from other surveyors
    remoteFindings.forEach((f) => {
      counts[f.domain] = (counts[f.domain] || 0) + 1
    })
    return counts
  }, [stepStates, remoteFindings])

  const totalFindings = useMemo(
    () => Object.values(domainCounts).reduce((sum, c) => sum + c, 0),
    [domainCounts],
  )

  // Area-level progress
  const areaProgress = useCallback(
    (ai: number) => {
      const area = SURVEY_WALKTHROUGH[ai]
      if (!area) return { total: 0, done: 0 }
      let total = 0
      let done = 0
      area.sections.forEach((sec, si) => {
        sec.items.forEach((_, ii) => {
          total++
          const st = stepStates[stepKey(ai, si, ii)]
          if (st && st.status !== 'pending') done++
        })
      })
      return { total, done }
    },
    [stepStates],
  )

  // Navigation
  const canGoNext = useCallback(() => {
    if (!currentArea) return false
    // Always allow Next when a No finding is pending — button saves the finding
    // (and navigates forward) or shows validation error; never silent no-op.
    if (areaPhase === 'inspection' && (checklistResponse === 'no' || Object.values(checklistResponses).includes('no'))) return true
    // Can advance within items
    if (currentSection && itemIdx < currentSection.items.length - 1) return true
    // Can advance to next section
    if (currentArea && sectionIdx < currentArea.sections.length - 1) return true
    // Can advance to next area
    if (areaIdx < SURVEY_WALKTHROUGH.length - 1) return true
    return false
  }, [areaIdx, sectionIdx, itemIdx, currentArea, currentSection, areaPhase, checklistResponse, checklistResponses])

  const canGoPrev = useCallback(() => {
    return areaIdx > 0 || sectionIdx > 0 || itemIdx > 0
  }, [areaIdx, sectionIdx, itemIdx])

  const scrollToTop = useCallback(() => {
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0 })
      // Double-fire to catch post-render layout shifts
      setTimeout(() => window.scrollTo({ top: 0 }), 50)
    })
  }, [])

  const resetStepInputs = useCallback(() => {
    scrollToTop()
    setObservation('')
    setPhotoIds([])
    setPromptEvidence({})
    setPromptEvidenceOpen({})
    setSeverity(2)
    setShowNarrative(true)
    setChecklistResponse(null)
    setChecklistResponses({})
    setEvidenceError(null)
  }, [scrollToTop])

  const goNext = useCallback(() => {
    resetStepInputs()

    const area = SURVEY_WALKTHROUGH[areaIdx]
    if (!area) return

    const section = area.sections[sectionIdx]

    // Can advance within current section's items
    if (section && itemIdx < section.items.length - 1) {
      setItemIdx(itemIdx + 1)
      return
    }

    // Can advance to next section in same area
    if (sectionIdx < area.sections.length - 1) {
      setSectionIdx(sectionIdx + 1)
      setItemIdx(0)
      return
    }

    // Can advance to next area
    if (areaIdx < SURVEY_WALKTHROUGH.length - 1) {
      setAreaIdx(areaIdx + 1)
      setSectionIdx(0)
      setItemIdx(0)
      setAreaPhase('briefing')
      setCurrentRoom(null)
      return
    }

    // Already at the very last item.
    setReviewingCompletedItem(false)
    setShowCompletionSummary(true)
  }, [areaIdx, sectionIdx, itemIdx, resetStepInputs])

  const goPrev = useCallback(() => {
    setShowCompletionSummary(false)
    setReviewingCompletedItem(false)
    resetStepInputs()

    // Can go back within current section's items
    if (itemIdx > 0) {
      setItemIdx(itemIdx - 1)
      return
    }

    // Can go back to previous section in same area
    if (sectionIdx > 0) {
      const prevSection = SURVEY_WALKTHROUGH[areaIdx].sections[sectionIdx - 1]
      setSectionIdx(sectionIdx - 1)
      setItemIdx(prevSection.items.length - 1)
      return
    }

    // Can go back to previous area's last item
    if (areaIdx > 0) {
      const prevArea = SURVEY_WALKTHROUGH[areaIdx - 1]
      const lastSec = prevArea.sections[prevArea.sections.length - 1]
      setAreaIdx(areaIdx - 1)
      setSectionIdx(prevArea.sections.length - 1)
      setItemIdx(lastSec.items.length - 1)
      setAreaPhase('inspection')
      return
    }

    // Already at the very first item — do nothing
  }, [areaIdx, sectionIdx, itemIdx, resetStepInputs])

  const jumpToArea = useCallback((ai: number) => {
    setShowCompletionSummary(false)
    setReviewingCompletedItem(false)
    scrollToTop()
    setAreaIdx(ai)
    setSectionIdx(0)
    setItemIdx(0)
    setObservation('')
    setPhotoIds([])
    setPromptEvidence({})
    setPromptEvidenceOpen({})
    setSeverity(1)
    setChecklistResponse(null)
    setChecklistResponses({})
    setEvidenceError(null)
    setShowNarrative(true)
    setShowAreaNav(false)
    setAreaPhase('briefing')
    setCurrentRoom(null)
  }, [scrollToTop])

  const jumpToSection = useCallback((ai: number, si: number) => {
    setShowCompletionSummary(false)
    setReviewingCompletedItem(false)
    scrollToTop()
    setAreaIdx(ai)
    setSectionIdx(si)
    setItemIdx(0)
    setObservation('')
    setPhotoIds([])
    setPromptEvidence({})
    setPromptEvidenceOpen({})
    setSeverity(1)
    setChecklistResponse(null)
    setChecklistResponses({})
    setEvidenceError(null)
    setShowNarrative(true)
    setShowAreaNav(false)
    setAreaPhase('inspection')
    setCurrentRoom(null)
  }, [scrollToTop])

  const jumpToItem = useCallback((ai: number, si: number, ii: number) => {
    const state = stepStates[stepKey(ai, si, ii)]
    setShowCompletionSummary(false)
    setReviewingCompletedItem(true)
    scrollToTop()
    setAreaIdx(ai)
    setSectionIdx(si)
    setItemIdx(ii)
    setAreaPhase('inspection')
    setCurrentRoom(null)
    setObservation(state?.notes ?? '')
    setPhotoIds(state?.photoIds ?? [])
    setPromptEvidence(state?.promptEvidence ?? {})
    setPromptEvidenceOpen(
      Object.fromEntries(
        Object.entries(state?.checklistResponses ?? {})
          .filter(([, response]) => response === 'no')
          .map(([promptId]) => [promptId, true]),
      ),
    )
    setSeverity(2)
    setChecklistResponse(state?.status === 'finding' ? 'no' : state?.status === 'skipped' ? 'na' : state?.status === 'clear' ? 'yes' : null)
    setChecklistResponses(state?.checklistResponses ?? {})
    setEvidenceError(null)
    setShowNarrative(true)
  }, [scrollToTop, stepStates])

  const markCurrentStepClear = useCallback(
    (
      status: 'clear' | 'skipped',
      responses?: Record<string, SavedChecklistResponse>,
      nextPromptEvidence: Record<string, PromptEvidence> = {},
    ) => {
    const nextStepStates = {
      ...stepStates,
      [currentKey]: {
        status,
        findingIds: [],
        photoIds: [],
        notes: '',
        checklistResponses: responses ?? checklistResponses,
        promptEvidence: nextPromptEvidence,
      },
    }
    setStepStates(nextStepStates)
    setReviewingCompletedItem(false)
    if (session) {
      const nextPosition = getNextWalkthroughPosition(areaIdx, sectionIdx, itemIdx, currentRoom)
      void persistWalkthroughState({
        survey_id: session.survey_id,
        ...nextPosition,
        stepStates: nextStepStates,
        lastUpdated: new Date().toISOString(),
      })
    }
    if (isLastInspectionItem) {
      setShowCompletionSummary(true)
      return
    }
    goNext()
  }, [areaIdx, checklistResponses, currentKey, currentRoom, goNext, isLastInspectionItem, itemIdx, persistWalkthroughState, sectionIdx, session, stepStates])

  // Mark step as clear (no finding)
  const markClear = useCallback(() => {
    setChecklistResponse('yes')
    const responses = checklistPrompts.length > 1
      ? Object.fromEntries(checklistPrompts.map((prompt) => [prompt.id, 'yes' as const]))
      : { 'check-main': 'yes' as const }
    setChecklistResponses(responses)
    markCurrentStepClear('clear', responses, {})
  }, [checklistPrompts, markCurrentStepClear])

  // Skip step
  const markSkipped = useCallback(() => {
    setChecklistResponse('na')
    const responses = checklistPrompts.length > 1
      ? Object.fromEntries(checklistPrompts.map((prompt) => [prompt.id, 'na' as const]))
      : { 'check-main': 'na' as const }
    setChecklistResponses(responses)
    setPromptEvidence({})
    setPromptEvidenceOpen({})
    markCurrentStepClear('skipped', responses, {})
  }, [checklistPrompts, markCurrentStepClear])

  const answerChecklistPrompt = useCallback((promptId: string, response: SavedChecklistResponse) => {
    const nextResponses = { ...checklistResponses, [promptId]: response }
    const nextPromptEvidence = {
      ...promptEvidence,
      [promptId]: promptEvidence[promptId] ?? { note: '', photoIds: [] },
    }
    setPromptEvidence(nextPromptEvidence)
    setPromptEvidenceOpen((prev) => ({
      ...prev,
      [promptId]: response === 'no',
    }))
    setChecklistResponses(nextResponses)
    setEvidenceError(null)

    // Persist partial progress to stepStates + IndexedDB on every click so
    // navigating away and back restores answers for multi-prompt items.
    const existing = stepStates[currentKey]
    const hasNo = Object.values(nextResponses).includes('no')
    const allAnswered = checklistPrompts.length > 0
      && checklistPrompts.every((prompt) => nextResponses[prompt.id])
    let nextStatus: StepState['status']
    if (allAnswered && !hasNo) {
      nextStatus = checklistPrompts.every((prompt) => nextResponses[prompt.id] === 'na') ? 'skipped' : 'clear'
    } else if (hasNo) {
      nextStatus = existing?.status ?? 'pending'
    } else {
      nextStatus = 'pending'
    }
    const nextStepStates = {
      ...stepStates,
      [currentKey]: {
        status: nextStatus,
        findingIds: existing?.findingIds ?? [],
        photoIds: existing?.photoIds ?? [],
        notes: existing?.notes ?? '',
        checklistResponses: nextResponses,
        promptEvidence: nextPromptEvidence,
      },
    }
    setStepStates(nextStepStates)
    if (session) {
      const nextPosition = getNextWalkthroughPosition(areaIdx, sectionIdx, itemIdx, currentRoom)
      void persistWalkthroughState({
        survey_id: session.survey_id,
        ...nextPosition,
        stepStates: nextStepStates,
        lastUpdated: new Date().toISOString(),
      })
    }

    if (response === 'no' || hasNo) {
      setChecklistResponse('no')
      return
    }

    if (!allAnswered) {
      setChecklistResponse(null)
      return
    }

    const status = checklistPrompts.every((prompt) => nextResponses[prompt.id] === 'na') ? 'skipped' : 'clear'
    setChecklistResponse(status === 'skipped' ? 'na' : 'yes')
    markCurrentStepClear(status, nextResponses, nextPromptEvidence)
  }, [areaIdx, checklistPrompts, checklistResponses, currentKey, currentRoom, itemIdx, markCurrentStepClear, persistWalkthroughState, promptEvidence, sectionIdx, session, stepStates])

  // Save a finding for the current step — auto-tagged with area + domain
  const noPromptAnswers = checklistPrompts.filter((prompt) => checklistResponses[prompt.id] === 'no')
  const needsNoEvidence = noPromptAnswers.some((prompt) => {
    const evidence = promptEvidence[prompt.id]
    return !evidence?.note?.trim() || evidence.severity == null
  })

  const saveFinding = useCallback(async () => {
    if (!session || !visitId || !currentArea || !currentSection || !currentItem) return
    const noPrompts = checklistPrompts.filter((prompt) => checklistResponses[prompt.id] === 'no')
    const findingInputs = noPrompts.length > 0
      ? noPrompts.map((prompt) => {
        const evidence = promptEvidence[prompt.id]
        const evidenceNote = (evidence?.note ?? '').trim()
        const findingObservation = noPrompts.length === 1
          ? evidenceNote || observation.trim()
          : `${prompt.text}: ${evidenceNote || observation.trim()}`
        return {
          observation: findingObservation.trim(),
          severity: evidence?.severity ?? severity,
          photoIds: evidence?.photoIds?.length ? evidence.photoIds : photoIds,
        }
      })
      : [{
        observation: observation.trim(),
        severity,
        photoIds,
      }]

    setIsSaving(true)
    setEvidenceError(null)

    try {
      const findings: PendingFinding[] = findingInputs.map((input) => ({
        finding_id: crypto.randomUUID(),
        survey_id: session.survey_id,
        visit_id: visitId,
        domain: currentSection.domain,
        standard_ref: currentItem.cmsTag,
        severity: input.severity,
        observation: input.observation,
        area: currentArea.area,
        photo_ids: input.photoIds,
        audio_transcript: null,
        created_at: new Date().toISOString(),
      }))

      for (const finding of findings) {
        await surveyStore.saveFinding(finding)
        await enqueueFinding(visitId, session.survey_id, finding.finding_id)
      }

      const nextStepStates = {
        ...stepStates,
        [currentKey]: {
          status: 'finding' as const,
          findingIds: [...(stepStates[currentKey]?.findingIds ?? []), ...findings.map((finding) => finding.finding_id)],
          photoIds: [...new Set([...(stepStates[currentKey]?.photoIds ?? []), ...findings.flatMap((finding) => finding.photo_ids)])],
          notes: findings.map((finding) => finding.observation).join('\n\n'),
          checklistResponses,
          promptEvidence,
        },
      }
      setStepStates(nextStepStates)

      const nextPosition = getNextWalkthroughPosition(areaIdx, sectionIdx, itemIdx, currentRoom)
      await persistWalkthroughState({
        survey_id: session.survey_id,
        ...nextPosition,
        stepStates: nextStepStates,
        lastUpdated: new Date().toISOString(),
      })

      // Run pattern and condition-level detection
      const allFindings = await surveyStore.getFindingsBySurvey(session.survey_id)
      const analyzable: AnalyzableFinding[] = allFindings.map((f) => ({
        finding_id: f.finding_id,
        domain: f.domain,
        standard_ref: f.standard_ref,
        severity: f.severity,
        observation: f.observation,
        area: f.area,
      }))

      const patterns = detectPatterns(analyzable)
      const thisRefPatterns = patterns.filter((p) => p.standardRef === currentItem.cmsTag && p.count >= 3)
      if (thisRefPatterns.length > 0) {
        setPatternToast(`Pattern detected: ${thisRefPatterns[0].count} findings under ${thisRefPatterns[0].standardRef}. Consider upgrading to Moderate severity.`)
        setTimeout(() => setPatternToast(null), 6000)
      }

      const conditions = detectConditionLevel(analyzable)
      if (conditions.length > 0 && findings.some((finding) => conditions[0].findingIds.includes(finding.finding_id))) {
        setConditionAlert(conditions[0])
      }

      if (isLastInspectionItem) {
        setReviewingCompletedItem(false)
        setShowCompletionSummary(true)
      } else {
        goNext()
      }
    } finally {
      setIsSaving(false)
    }
  }, [session, visitId, currentArea, currentSection, currentItem, observation, severity, photoIds, currentKey, goNext, stepStates, areaIdx, sectionIdx, itemIdx, currentRoom, isLastInspectionItem, checklistPrompts, checklistResponses, promptEvidence, persistWalkthroughState])

  // ── Render ──────────────────────────────────────────────────────────────

  if (!visitId || (!session && sessionLoading)) {
    if (resumeFacility !== null) {
      return <HostHandoffSplash facility={resumeFacility || undefined} />
    }
    return (
      <div className="px-4 py-6">
        <p className="text-sm text-slate-500">Loading guided survey...</p>
      </div>
    )
  }

  if (!session) {
    if (resumeFacility !== null) {
      return <HostHandoffSplash facility={resumeFacility || undefined} />
    }
    return (
      <div className="px-4 py-6">
        <p className="text-sm text-slate-500">
          {isFieldSurveyor ? 'No active survey. Start one from Today.' : 'No active survey. Start one from the dashboard.'}
        </p>
        <Link to={isFieldSurveyor ? '/today' : '..'} className="btn-ghost text-sm mt-4 inline-block">
          {isFieldSurveyor ? 'Back to Today' : 'Back to Dashboard'}
        </Link>
      </div>
    )
  }

  const shouldShowCompletionSummary = showCompletionSummary
    || (!completedResumeRequestedRef.current && !reviewingCompletedItem && totalItems > 0 && completedItems >= totalItems)

  if (!currentArea || !currentSection || !currentItem || shouldShowCompletionSummary) {
    return (
      <div className="px-4 py-6 text-center space-y-4">
        <CheckCircle size={48} className="mx-auto text-emerald-500" />
        <h2 className="section-title">
          {assessmentCoverage.isFullyAssessed ? 'Survey Walkthrough Complete' : 'Ready to Submit'}
        </h2>
        <p className="text-sm text-slate-600">
          {assessmentCoverage.isFullyAssessed
            ? `You have reviewed all ${totalItems} inspection items across ${SURVEY_WALKTHROUGH.length} areas.`
            : assessmentCoverage.scopeSummary}
        </p>
        {assessmentCoverage.isPartial && (
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3 text-left" data-testid="walkthrough-partial-scope-note">
            {assessmentCoverage.notAssessedDisclaimer}
          </p>
        )}
        <div className="text-sm text-slate-500">
          <p>{assessmentCoverage.assessedItems} items assessed</p>
          <p>{assessmentCoverage.findingItems} findings recorded</p>
          <p>{assessmentCoverage.clearItems} marked clear</p>
          <p>{assessmentCoverage.skippedItems + assessmentCoverage.pendingItems} not assessed (excluded from scoring)</p>
        </div>
        {findingSummaries.length > 0 && (
          <div className="text-left space-y-2">
            <h3 className="text-sm font-bold text-slate-700">Exit summary top issues</h3>
            {findingSummaries.slice(0, 3).map((summary) => (
              <button
                key={summary.key}
                type="button"
                onClick={() => jumpToItem(summary.areaIdx, summary.sectionIdx, summary.itemIdx)}
                className="w-full rounded-lg border border-amber-200 bg-amber-50 p-3 text-left text-sm text-slate-700"
              >
                <span className="block font-bold text-amber-800">{summary.item.label}</span>
                <span className="block text-xs text-slate-600">{summary.area.areaLabel} · {summary.section.domainLabel}</span>
                <span className="block text-xs text-slate-600">{summary.state.photoIds.length} photo(s) · {summary.state.notes}</span>
              </button>
            ))}
          </div>
        )}
        <Link
          to="../complete"
          relative="path"
          data-testid="walkthrough-complete-survey-link"
          className="btn-primary inline-flex items-center justify-center gap-2 w-full max-w-sm mx-auto"
        >
          <FileCheck size={16} />
          Complete Survey &amp; Generate Report
        </Link>
        <Link to=".." className="btn-ghost text-sm inline-block">Back to Dashboard</Link>
      </div>
    )
  }

  const statusIcon = (status: StepStatus | undefined) => {
    switch (status) {
      case 'finding': return <AlertTriangle size={14} className="text-amber-500" />
      case 'clear': return <CheckCircle size={14} className="text-emerald-500" />
      case 'skipped': return <SkipForward size={14} className="text-slate-400" />
      default: return <div className="w-3.5 h-3.5 rounded-full border-2 border-slate-300" />
    }
  }

  return (
    <FieldAppShell
      eyebrow="Guided Survey"
      title="Survey Walkthrough"
      subtitle={currentArea.areaLabel}
      progress={progressPct}
      progressLabel={`${completedItems} of ${totalItems} items`}
      backTo={isFieldSurveyor ? '/today' : '..'}
      backLabel={isFieldSurveyor ? 'Back to today' : 'Back to dashboard'}
      rightSlot={
        <div className="flex items-center gap-2">
          <Link
            to="../complete"
            relative="path"
            data-testid="walkthrough-submit-complete-link"
            className="inline-flex items-center gap-1 rounded-lg bg-white/15 px-2 py-1.5 text-[10px] font-extrabold uppercase tracking-wide text-white hover:bg-white/25"
            title="Complete survey and generate report (partial submit allowed)"
          >
            <FileCheck size={12} />
            Submit
          </Link>
          <SyncStatus />
        </div>
      }
      contentClassName="space-y-3 px-4 py-4 pb-44"
    >

      {/* ── Resume toast ───────────────────────────────────────────────── */}
      {resumed && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-2 text-sm text-blue-700">
          <CheckCircle size={16} />
          Resumed from where you left off ({completedItems} items completed)
        </div>
      )}

      {/* ── Finding counter ─────────────────────────────────────────────── */}
      <FindingCounter domainCounts={domainCounts} totalFindings={totalFindings} />

      {/* ── Pattern toast ───────────────────────────────────────────────── */}
      {patternToast && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-xs text-amber-800 font-semibold">
          <AlertTriangle size={14} className="inline mr-1" />
          {patternToast}
        </div>
      )}

      {/* ── Remote finding toast (multi-surveyor) ────────────────────────── */}
      {remoteToasts.length > 0 && (
        <div className="bg-indigo-50 border border-indigo-300 rounded-lg p-3 text-xs text-indigo-800 font-semibold">
          <MapPin size={14} className="inline mr-1" />
          {remoteToasts[0].surveyor_name ?? 'Another surveyor'} added a finding in {remoteToasts[0].domain}
          {remoteToasts.length > 1 && ` (+${remoteToasts.length - 1} more)`}
        </div>
      )}

      {/* ── Condition-level alert modal ──────────────────────────────────── */}
      {conditionAlert && (
        <div className="fixed inset-0 bg-black/50 z-[90] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-5 max-w-sm w-full space-y-3">
            <div className={`flex items-center gap-2 ${conditionAlert.severity === 'critical' ? 'text-red-600' : 'text-amber-600'}`}>
              <AlertTriangle size={20} />
              <span className="text-sm font-bold">
                {conditionAlert.severity === 'critical' ? 'Condition-Level Alert' : 'Pattern Warning'}
              </span>
            </div>
            <p className="text-xs text-slate-700 leading-relaxed">{conditionAlert.description}</p>
            <div className="text-[10px] text-slate-500">
              <p>CfC: {conditionAlert.cfcSection} — {conditionAlert.cfcTitle}</p>
              <p>{conditionAlert.findingCount} findings across {conditionAlert.standardRefs.length} standard(s)</p>
            </div>
            <button
              type="button"
              onClick={() => setConditionAlert(null)}
              className="w-full py-2.5 rounded-lg text-sm font-bold bg-[var(--brand-primary)] text-white"
            >
              Acknowledged
            </button>
          </div>
        </div>
      )}

      {/* ── Photo review overlay ────────────────────────────────────────── */}
      {reviewPhoto && (
        <PhotoReview
          key={reviewPhoto.photoId}
          photoUrl={reviewPhoto.url}
          annotations={reviewAnnotations}
          requireCommentedPin={reviewPhoto.requirePin}
          onSave={async (annotations) => {
            await persistReviewAnnotations(reviewPhoto.photoId, annotations)
            applyPinEvidenceToPrompt(reviewPhoto.promptId, reviewPhoto.photoId, annotations)
            setReviewPhoto(null)
          }}
          onClose={async (annotations) => {
            // Preserve whatever pins the user placed, even on dismiss — so
            // closing from the overlay after Done/tapping X doesn't drop the
            // pin state. See PhotoReview.onClose docstring.
            await persistReviewAnnotations(reviewPhoto.photoId, annotations)
            applyPinEvidenceToPrompt(reviewPhoto.promptId, reviewPhoto.photoId, annotations)
            setReviewPhoto(null)
          }}
        />
      )}

      {/* ── Area Briefing Phase ─────────────────────────────────────────── */}
      {areaPhase === 'briefing' && currentArea && (
        <AreaBriefing
          area={currentArea}
          areasCompleted={areaIdx}
          totalAreas={SURVEY_WALKTHROUGH.length}
          onBegin={() => {
            if (currentArea.isRepeatable && currentArea.repeatableRooms && !currentRoom) {
              // Show room selector — for now just default to first room
              setCurrentRoom(currentArea.repeatableRooms[0])
            }
            setAreaPhase(currentArea.requiredPhotos.length > 0 ? 'photos' : 'inspection')
          }}
          onSkip={() => {
            // Skip entire area — advance to next
            if (areaIdx < SURVEY_WALKTHROUGH.length - 1) {
              setAreaIdx(areaIdx + 1)
              setSectionIdx(0)
              setItemIdx(0)
              setAreaPhase('briefing')
            }
          }}
        />
      )}

      {/* ── Photo Checklist Phase ───────────────────────────────────────── */}
      {areaPhase === 'photos' && currentArea && session && (
        <PhotoChecklist
          requirements={currentArea.requiredPhotos}
          visitId={visitId}
          surveyId={session.survey_id}
          area={currentRoom as any ?? currentArea.area}
          areaLabel={currentRoom ?? currentArea.areaLabel}
          onPhotoCapture={(photo, requirementIdx) => {
            if (photo.blob) {
              const angle = currentArea.requiredPhotos[requirementIdx]?.angle
              const requirePin = angle === 'closeup'
              void blobToDataUrl(photo.blob).then((url) => openReview(url, photo.photo_id, requirePin))
            }
          }}
          onReviewPhoto={(photoId, blobUrl) => {
            // Re-opening a checklist photo from the gallery — look up the
            // requirement angle to decide whether a pin is required on save.
            // We don't have the requirementIdx here; default to false (baseline)
            // since closeup photos are typically annotated at first capture.
            openReview(blobUrl, photoId, false)
          }}
          onComplete={() => setAreaPhase('inspection')}
        />
      )}

      {/* ── Inspection Phase ────────────────────────────────────────────── */}
      {areaPhase === 'inspection' && (
      <>

      {/* ── Area navigator (collapsible) ────────────────────────────────── */}
      <button
        type="button"
        onClick={() => setShowAreaNav(!showAreaNav)}
        className="w-full app-surface p-3 flex items-center justify-between"
      >
        <div className="flex items-center gap-2">
          <MapPin size={16} className="text-[var(--brand-primary)]" />
          <span className="text-sm font-bold text-slate-700">{currentArea.areaLabel}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">
            {areaProgress(areaIdx).done}/{areaProgress(areaIdx).total}
          </span>
          {showAreaNav ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </button>

      {showAreaNav && (
        <div className="app-surface p-2 space-y-0.5 max-h-80 overflow-y-auto">
          {SURVEY_WALKTHROUGH.map((aw, ai) => {
            const prog = areaProgress(ai)
            const isActive = ai === areaIdx
            return (
              <div key={aw.area}>
                <button
                  type="button"
                  onClick={() => jumpToArea(ai)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center justify-between transition-colors ${
                    isActive
                      ? 'bg-blue-50 text-[var(--brand-primary)] font-semibold'
                      : 'hover:bg-slate-50 text-slate-700'
                  }`}
                >
                  <span>{aw.areaLabel}</span>
                  <span className="text-xs text-slate-400">
                    {prog.done}/{prog.total}
                  </span>
                </button>
                {isActive && aw.sections.length > 1 && (
                  <div className="ml-4 border-l-2 border-slate-200 pl-2 space-y-0.5 my-1">
                    {aw.sections.map((sec, si) => {
                      const secActive = si === sectionIdx
                      const secDone = sec.items.every((_, ii) => {
                        const st = stepStates[stepKey(ai, si, ii)]
                        return st && st.status !== 'pending'
                      })
                      return (
                        <button
                          key={sec.domain}
                          type="button"
                          onClick={() => jumpToSection(ai, si)}
                          className={`w-full text-left px-2 py-1.5 rounded text-xs flex items-center justify-between transition-colors ${
                            secActive
                              ? 'bg-blue-100 text-[var(--brand-primary)] font-semibold'
                              : 'hover:bg-slate-50 text-slate-600'
                          }`}
                        >
                          <span className="truncate">{sec.domainLabel}</span>
                          <span className={`text-[10px] ${secDone ? 'text-emerald-500' : 'text-slate-400'}`}>
                            {sec.items.length} items
                          </span>
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Area intro replaced by AreaBriefing interstitial (shown in briefing phase) */}

      {/* ── Domain badge ─────────────────────────────────────────────── */}
      <div className="flex items-center gap-2">
        <span className="brand-chip text-xs font-bold">{currentSection.domain}</span>
        <span className="text-xs text-slate-500">{currentSection.domainLabel}</span>
        <span className="ml-auto text-xs text-slate-400">
          Item {itemIdx + 1} of {currentSection.items.length}
        </span>
      </div>

      {/* ── Inspection item card ──────────────────────────────────────── */}
      <div className="app-surface p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <h2 className="text-sm font-bold text-slate-800">{currentItem.label}</h2>
          {currentState && statusIcon(currentState.status)}
        </div>

        {/* Narrative toggle */}
        <button
          type="button"
          onClick={() => setShowNarrative(!showNarrative)}
          className="text-xs font-semibold text-[var(--brand-primary)] flex items-center gap-1"
        >
          <List size={12} />
          {showNarrative ? 'Hide guidance' : 'Show guidance — what to look for'}
        </button>

        {showNarrative && (
          <div className="bg-blue-50 rounded-lg p-3 space-y-2">
            <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">
              {currentItem.narrative}
            </p>
            <div className="flex flex-wrap gap-2 text-[10px] font-mono text-slate-500">
              <span className="bg-white rounded px-1.5 py-0.5">CMS: {currentItem.cmsTag}</span>
              {currentItem.tjcRef && (
                <span className="bg-white rounded px-1.5 py-0.5">TJC: {currentItem.tjcRef}</span>
              )}
              {currentItem.aaahcRef && (
                <span className="bg-white rounded px-1.5 py-0.5">AAAHC: {currentItem.aaahcRef}</span>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5 pt-1">
              {currentItem.evidence.map((ev) => (
                <span key={ev} className="text-[10px] bg-slate-200 text-slate-600 rounded-full px-2 py-0.5">
                  {ev === 'photo' && 'Photo recommended'}
                  {ev === 'document' && 'Document review'}
                  {ev === 'interview' && 'Staff interview'}
                  {ev === 'observation' && 'Visual observation'}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Checklist response ───────────────────────────────────────── */}
      <div className="app-surface p-4 space-y-3">
        <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
          Checklist Response
        </span>
        {checklistPrompts.length > 1 && (
          <p className="text-xs text-slate-500">
            This item has multiple checks. Answer each one separately so the walkthrough captures the data individually.
          </p>
        )}
        <div className="space-y-3" role="group" aria-label="Checklist response">
          {checklistPrompts.map((prompt) => {
            const promptResponse = checklistResponses[prompt.id]
            const isLegacyFindingPrompt = checklistResponse === 'no'
              && Object.keys(checklistResponses).length === 0
              && checklistPrompts[0]?.id === prompt.id
            const isYes = checklistPrompts.length === 1 ? checklistResponse === 'yes' : promptResponse === 'yes'
            const isNo = checklistPrompts.length === 1 ? checklistResponse === 'no' : promptResponse === 'no' || isLegacyFindingPrompt
            const isNa = checklistPrompts.length === 1 ? checklistResponse === 'na' : promptResponse === 'na'
            const isOpen = isNo || promptEvidenceOpen[prompt.id] === true
            const currentPromptEvidence = promptEvidence[prompt.id] ?? (isLegacyFindingPrompt
              ? { note: observation, photoIds }
              : { note: '', photoIds: [] })
            const promptTiles = capturedTiles.filter((tile) => currentPromptEvidence.photoIds.includes(tile.photoId))
            return (
              <div key={prompt.id} className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
                <p className="text-sm font-semibold text-slate-700">{prompt.text}</p>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      if (checklistPrompts.length === 1) {
                        markClear()
                        return
                      }
                      answerChecklistPrompt(prompt.id, 'yes')
                    }}
                    className={`rounded-lg py-3 text-sm font-bold transition-colors ${
                      (checklistPrompts.length === 1 ? checklistResponse === 'yes' : promptResponse === 'yes')
                        ? 'bg-emerald-600 text-white'
                        : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                    }`}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      answerChecklistPrompt(prompt.id, 'no')
                    }}
                    className={`rounded-lg py-3 text-sm font-bold transition-colors ${
                      (checklistPrompts.length === 1 ? checklistResponse === 'no' : promptResponse === 'no')
                        ? 'bg-amber-600 text-white'
                        : 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                    }`}
                  >
                    No
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (checklistPrompts.length === 1) {
                        markSkipped()
                        return
                      }
                      answerChecklistPrompt(prompt.id, 'na')
                    }}
                    className={`rounded-lg py-3 text-sm font-bold transition-colors ${
                      (checklistPrompts.length === 1 ? checklistResponse === 'na' : promptResponse === 'na')
                        ? 'bg-slate-700 text-white'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                  >
                    N/A
                  </button>
                </div>
                <div className="space-y-2 pt-2">
                  <button
                    type="button"
                    className="text-xs font-bold uppercase tracking-wider text-slate-500 disabled:opacity-40"
                    disabled={!isYes && !isNa}
                    onClick={() => {
                      if (isNo) {
                        return
                      }
                      setPromptEvidenceOpen((prev) => ({
                        ...prev,
                        [prompt.id]: !prev[prompt.id],
                      }))
                    }}
                  >
                    {isYes && (isOpen ? 'Hide evidence' : 'Add note or photo')}
                    {isNa && (isOpen ? 'Hide note' : 'Add note')}
                    {isNo && 'No Response Evidence'}
                  </button>

                  {(isYes || isNa || isNo) && isOpen ? (
                    <div className="space-y-2">
                      {(isYes || isNo) && (
                        <PhotoCapture
                          visitId={visitId}
                          surveyId={session.survey_id}
                          area={currentArea.area}
                          variant="dashed-tile"
                          label={isNo ? 'Add photo' : 'Add photo'}
                          requireDurableUpload={isNo}
                          onCapture={(photo) => {
                            setPhotoIds((prev) => [...prev, photo.photo_id])
                            setPromptEvidence((prev) => {
                              const existing = prev[prompt.id] ?? { note: '', photoIds: [] }
                              return {
                                ...prev,
                                [prompt.id]: {
                                  ...existing,
                                  photoIds: [...existing.photoIds, photo.photo_id],
                                },
                              }
                            })
                            if (photo.blob) {
                              void blobToDataUrl(photo.blob).then((url) => {
                                setReviewPhoto({ url, photoId: photo.photo_id, requirePin: isNo, promptId: prompt.id })
                              })
                            }
                          }}
                        />
                      )}
                      {(isYes || isNo) && promptTiles.length > 0 ? (
                        <div className="flex flex-wrap items-start gap-2">
                          {promptTiles.map((tile) => (
                            <div key={tile.photoId} className="relative">
                              <button
                                type="button"
                                onClick={() => void openReview(tile.url, tile.photoId, isNo, prompt.id)}
                                className="relative group block rounded-lg overflow-hidden border-2 border-slate-300 bg-slate-50 shadow-sm hover:border-[var(--brand-primary)] transition-colors"
                                title="Tap to review"
                                aria-label="Review captured photo"
                              >
                                <img src={tile.url} alt="Captured" className="w-16 h-16 object-cover" />
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                                  <Pencil size={14} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                </div>
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setPhotoIds((prev) => prev.filter((id) => id !== tile.photoId))
                                  setPromptEvidence((prev) => {
                                    const nextEvidence = prev[prompt.id] ?? { note: '', photoIds: [] }
                                    const nextPhotoAnnotations = { ...(nextEvidence.photoAnnotations ?? {}) }
                                    delete nextPhotoAnnotations[tile.photoId]
                                    return {
                                      ...prev,
                                      [prompt.id]: {
                                        ...nextEvidence,
                                        photoIds: nextEvidence.photoIds.filter((id) => id !== tile.photoId),
                                        photoAnnotations: nextPhotoAnnotations,
                                      },
                                    }
                                  })
                                  void surveyStore.removePhoto(tile.photoId).catch(() => { /* local-only delete */ })
                                  setReviewPhoto((prev) => (prev?.photoId === tile.photoId ? null : prev))
                                }}
                                aria-label="Remove captured photo"
                                title="Remove photo"
                                className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full border border-slate-200 bg-white text-red-500 shadow-sm hover:bg-red-50 hover:text-red-600"
                              >
                                <Trash2 size={12} />
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : null}
                      {isNo && (
                        <p className="text-xs text-slate-600">
                          If a photo is not possible (e.g. no poster present to photograph), describe the finding in the note below - a note is required.
                        </p>
                      )}
                      <div>
                        <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                          {isNo ? 'No response note' : isYes ? 'Supporting note' : 'Improvement note'}
                        </span>
                        <textarea
                          aria-label={isNo ? 'No response note' : `${prompt.id} note`}
                          placeholder={
                            isNo
                              ? 'Document what you observed that confirms noncompliance.'
                              : isYes
                                ? 'Document what you observed that confirms compliance.'
                                : 'Note why this does not apply, or any process improvement opportunity.'
                          }
                          value={currentPromptEvidence.note}
                          onChange={(event) => {
                            const value = event.target.value
                            setPromptEvidence((prev) => {
                              const existing = prev[prompt.id] ?? { note: '', photoIds: [] }
                              return {
                                ...prev,
                                [prompt.id]: {
                                  ...existing,
                                  note: value,
                                },
                              }
                            })
                            if (isNo) {
                              setEvidenceError(null)
                            }
                          }}
                          rows={3}
                          className="mt-2 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-[var(--brand-primary)] focus:outline-none focus:ring-2 focus:ring-blue-100 resize-none"
                        />
                      </div>
                      {isNo && (
                        <div className="space-y-1.5">
                          <span className="text-xs text-slate-500">CMS Deficiency Severity:</span>
                          <div className="flex gap-2">
                            {([1, 2, 3] as const).map((s) => {
                              const labels = { 1: 'Low', 2: 'Moderate', 3: 'High' } as const
                              const colors = {
                                1: currentPromptEvidence.severity === s ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200',
                                2: currentPromptEvidence.severity === s ? 'bg-amber-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200',
                                3: currentPromptEvidence.severity === s ? 'bg-red-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200',
                              }
                              return (
                                <button
                                  key={s}
                                  type="button"
                                  onClick={() => {
                                    setPromptEvidence((prev) => {
                                      const existing = prev[prompt.id] ?? { note: '', photoIds: [] }
                                      return {
                                        ...prev,
                                        [prompt.id]: {
                                          ...existing,
                                          severity: s,
                                        },
                                      }
                                    })
                                    setEvidenceError(null)
                                  }}
                                  className={`flex-1 py-2 rounded-lg text-xs font-bold transition-colors ${colors[s]}`}
                                >
                                  {s} — {labels[s]}
                                </button>
                              )
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      </>
      )}

      {/* ── Sticky bottom bar (always visible during survey) ────────── */}
      {/* z-[90] > FieldAppBottomNav z-[80] so Prev/Next stay visible and tappable (#1662) */}
      <div
        data-testid="walkthrough-footer-nav"
        className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] space-y-2 z-[90]"
      >
        {/* Nav row: Previous | Section Dropdown | Next (Next auto-saves
            any pending No finding before advancing — no separate save
            button needed). */}
        <div className="flex items-center justify-between gap-1">
          <button
            type="button"
            onClick={goPrev}
            disabled={!canGoPrev()}
            className="btn-ghost text-xs py-1.5 px-3 disabled:opacity-40"
          >
            <ArrowLeft size={12} className="inline mr-1" /> Prev
          </button>
          <button
            type="button"
            onClick={() => setShowBottomNav(!showBottomNav)}
            className="flex items-center gap-1 text-xs font-semibold text-[var(--brand-primary)] py-1.5 px-2 rounded-lg hover:bg-blue-50 transition-colors"
          >
            <List size={12} />
            <span className="max-w-[120px] truncate">{currentArea?.areaLabel ?? 'Jump to…'}</span>
            {showBottomNav ? <ChevronDown size={12} /> : <ChevronUp size={12} />}
          </button>
          <button
            type="button"
            onClick={() => {
              if (areaPhase === 'inspection' && (checklistResponse === 'no' || Object.values(checklistResponses).includes('no'))) {
                if (needsNoEvidence) {
                  setEvidenceError('Add a note and select severity for all No responses before continuing.')
                  return
                }
                void saveFinding()
              } else {
                goNext()
              }
            }}
            disabled={!canGoNext() || isSaving}
            className="btn-ghost text-xs py-1.5 px-3 disabled:opacity-40"
          >
            {isSaving ? 'Saving…' : <>Next <ArrowRight size={12} className="inline ml-1" /></>}
          </button>
        </div>
        {evidenceError && (
          <p className="text-xs font-semibold text-red-600 text-center">{evidenceError}</p>
        )}

        {/* Bottom section navigator */}
        {showBottomNav && (
          <div className="max-h-60 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg -mt-1">
            {SURVEY_WALKTHROUGH.map((aw, ai) => {
              const prog = areaProgress(ai)
              const isActiveArea = ai === areaIdx
              const isExpanded = expandedNavArea === ai
              return (
                <div key={aw.area}>
                  <button
                    type="button"
                    onClick={() => setExpandedNavArea(isExpanded ? null : ai)}
                    className={`w-full text-left px-3 py-2 text-xs flex items-center justify-between border-b border-slate-100 transition-colors ${
                      isActiveArea
                        ? 'bg-blue-50 text-[var(--brand-primary)] font-bold'
                        : 'hover:bg-slate-50 text-slate-700 font-medium'
                    }`}
                  >
                    <span className="truncate">{aw.areaLabel}</span>
                    <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                      <span className="text-[10px] text-slate-400">
                        {prog.done}/{prog.total}
                      </span>
                      {isExpanded ? <ChevronDown size={12} /> : <ChevronUp size={12} />}
                    </div>
                  </button>
                  {isExpanded && (
                    <div className="bg-slate-50">
                      {aw.sections.map((sec, si) => {
                        const secActive = isActiveArea && si === sectionIdx
                        return (
                          <button
                            key={sec.domain + si}
                            type="button"
                            onClick={() => { jumpToSection(ai, si); setShowBottomNav(false); setExpandedNavArea(null) }}
                            className={`w-full text-left pl-6 pr-3 py-1.5 text-[11px] flex items-center justify-between border-b border-slate-100 transition-colors ${
                              secActive
                                ? 'bg-blue-100 text-[var(--brand-primary)] font-semibold'
                                : 'hover:bg-slate-100 text-slate-600'
                            }`}
                          >
                            <span className="truncate">{sec.domainLabel}</span>
                            <span className="text-[10px] text-slate-400 ml-2 flex-shrink-0">
                              {sec.items.length}
                            </span>
                          </button>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {/* Existing complete path — discoverable mid-walkthrough (partial always allowed) */}
        <Link
          to="../complete"
          relative="path"
          data-testid="walkthrough-footer-complete-link"
          className="mt-1 flex w-full items-center justify-center gap-2 rounded-lg border border-[var(--brand-primary)] bg-white py-2.5 text-sm font-bold text-[var(--brand-primary)] hover:bg-blue-50"
        >
          <FileCheck size={16} />
          Complete Survey &amp; Generate Report
          {assessmentCoverage.isPartial && (
            <span className="text-[10px] font-semibold text-slate-500">
              ({assessmentCoverage.assessedItems}/{assessmentCoverage.totalItems} assessed)
            </span>
          )}
        </Link>
      </div>
    </FieldAppShell>
  )
}
